import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, Badge, Button, Input, Select, Field, Switch, SectionTitle } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { adoptToken } from '../lib/auth';
import type { ApiResponse } from '../lib/types';

// Fallback caso /whitelabel/brands falhe — a fonte da verdade são as marcas
// (embutidas + dinâmicas provisionadas no Settings > White-label Domains)
const LABEL_OPTIONS_FALLBACK = ['pixeldmp', 'trackdmp', 'perfdata'];
const DEFAULT_TIERS: Tier[] = [
  { min: 0, max: 3000, cpc: 0.40 }, { min: 3001, max: 15000, cpc: 0.36 }, { min: 15001, max: 30000, cpc: 0.33 },
  { min: 30001, max: 50000, cpc: 0.30 }, { min: 50001, max: 100000, cpc: 0.27 }, { min: 100001, max: 200000, cpc: 0.24 },
  { min: 200001, max: 300000, cpc: 0.22 }, { min: 300001, max: 500000, cpc: 0.21 }, { min: 500001, max: 1000000, cpc: 0.20 },
  { min: 1000001, max: 1500000, cpc: 0.19 }, { min: 1500001, max: 2000000, cpc: 0.19 }, { min: 2000001, max: 2500000, cpc: 0.18 },
  { min: 2500001, max: 3000000, cpc: 0.18 }, { min: 3000001, max: '', cpc: 0.17 },
];

interface Tier { min: number | string; max: number | string; cpc: number | string }
interface ClientUser { id?: number; email?: string; status?: string; cognito_user_id?: string; created_at?: string }
interface NewUser { email: string; password: string; status: string; role: string }
interface ClientData {
  id?: number; name?: string; label?: string; status?: string; cpc?: number | string;
  fiscal_data?: { cnpj?: string; company_name?: string; trade_name?: string } | null;
  cpc_tiers?: Tier[]; branding_enabled?: boolean; branding_cpc?: number | string; branding_cpc_tiers?: Tier[];
  users?: ClientUser[];
}

function TiersEditor({ tiers, onChange }: { tiers: Tier[]; onChange: (t: Tier[]) => void }) {
  const upd = (i: number, k: keyof Tier, v: string) => { const next = tiers.map((t) => ({ ...t })); next[i][k] = v; onChange(next); };
  return (
    <div>
      <div className="space-y-1.5">
        {tiers.map((t, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input type="number" value={t.min} onChange={(e) => upd(i, 'min', e.target.value)} placeholder="min" className="w-28" />
            <span className="text-slate-300">–</span>
            <Input type="number" value={t.max} onChange={(e) => upd(i, 'max', e.target.value)} placeholder="max" className="w-28" />
            <span className="text-slate-400 text-xs">$</span>
            <Input type="number" step={0.0001} value={t.cpc} onChange={(e) => upd(i, 'cpc', e.target.value)} placeholder="cpc" className="w-28" />
            <button type="button" onClick={() => onChange(tiers.filter((_, j) => j !== i))} className="text-rose-500 hover:text-rose-700 px-2">×</button>
          </div>
        ))}
      </div>
      <Button variant="outline" className="mt-2" onClick={() => onChange([...tiers, { min: '', max: '', cpc: '' }])}>+ Add tier</Button>
    </div>
  );
}

export default function ClientForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [c, setC] = useState<ClientData>({ status: 'active', cpc_tiers: [], branding_cpc_tiers: [], users: [] });
  const [labelOptions, setLabelOptions] = useState<string[]>(LABEL_OPTIONS_FALLBACK);
  useEffect(() => {
    api.get<ApiResponse<{ label: string }[]>>('/whitelabel/brands')
      .then((r) => { const ls = (r.data || []).map((b) => b.label); if (ls.length) setLabelOptions(ls); })
      .catch(() => {});
  }, []);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newUsers, setNewUsers] = useState<NewUser[]>([]);

  const set = (patch: Partial<ClientData>) => setC((prev) => ({ ...prev, ...patch }));
  const fiscal = c.fiscal_data || {};

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    api.get<ApiResponse<ClientData>>(`/clients/get?id=${id}`)
      .then((r) => {
        const d = r.data || {};
        setC({ ...d, cpc_tiers: d.cpc_tiers || [], branding_cpc_tiers: d.branding_cpc_tiers || [], users: d.users || [], fiscal_data: d.fiscal_data || {} });
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  async function save() {
    setSaving(true); setError(null);
    const cleanTiers = (ts: Tier[]) => ts.filter((t) => t.cpc !== '' && t.cpc != null).map((t) => ({ min: Number(t.min) || 0, max: t.max === '' || t.max == null ? null : Number(t.max), cpc: Number(t.cpc) || 0 }));
    const payload = {
      id: c.id || null,
      name: c.name,
      fiscal_data: { cnpj: fiscal.cnpj || null, company_name: fiscal.company_name || null, trade_name: fiscal.trade_name || null },
      cpc: c.cpc || null,
      cpc_tiers: cleanTiers(c.cpc_tiers || []),
      label: c.label || null,
      status: c.status || 'active',
      branding_enabled: !!c.branding_enabled,
      branding_cpc: c.branding_cpc || null,
      branding_cpc_tiers: cleanTiers(c.branding_cpc_tiers || []),
      // o backend só CRIA usuários (createCognitoUser + INSERT client_users) — só enviamos os novos
      users: newUsers.filter((u) => u.email.trim() && u.password.length >= 8)
        .map((u) => ({ email: u.email.trim(), password: u.password, status: u.status || 'active', role: u.role || 'client' })),
    };
    try {
      const r = await api.post<ApiResponse<unknown>>('/clients/save', payload);
      if (r.success) navigate('/clients'); else setError(r.error || r.message || 'Failed to save');
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Failed to save'); } finally { setSaving(false); }
  }

  // Admin "loga como" o usuário: troca a sessão pelo token de impersonation
  // (1h, sem privilégios de admin) e recarrega a app já como o cliente.
  const [impersonating, setImpersonating] = useState<string | null>(null);
  async function loginAs(u: ClientUser) {
    if (!u.email && !u.cognito_user_id) return;
    setImpersonating(u.email || u.cognito_user_id || '');
    try {
      const r = await api.post<ApiResponse<{ token: string }>>('/clients/impersonate', {
        email: u.email || null,
        cognito_user_id: u.cognito_user_id || null,
      });
      const token = r.data?.token;
      if (!r.success || !token) throw new Error(r.error || r.message || 'Failed to impersonate');
      adoptToken(token);
      window.location.assign('/campaigns');
    } catch (e) {
      alert(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed to impersonate');
      setImpersonating(null);
    }
  }

  if (loading) return <Layout title="Client"><Card className="p-10 text-center text-slate-400">Loading…</Card></Layout>;

  return (
    <Layout title={isEdit ? 'Edit Client' : 'Add Client'} actions={<><Button variant="outline" onClick={() => navigate('/clients')}>Cancel</Button><Button onClick={save} disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Update' : 'Create')}</Button></>}>
      {error && <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3">{error}</div>}

      <Card className="p-5 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Name" className="md:col-span-2"><Input value={c.name || ''} onChange={(e) => set({ name: e.target.value })} placeholder="Client name" /></Field>
          <Field label="Status"><div className="pt-1"><Switch checked={c.status === 'active'} onChange={(v) => set({ status: v ? 'active' : 'inactive' })} labelOn="active" labelOff="inactive" /></div></Field>
          <Field label="Label">
            <Select value={c.label || ''} onChange={(e) => set({ label: e.target.value })}>
              <option value="">Select Label (Optional)</option>
              {labelOptions.map((l) => <option key={l} value={l}>{l}</option>)}
              {c.label && !labelOptions.includes(c.label) && <option value={c.label}>{c.label} (current)</option>}
            </Select>
          </Field>
        </div>
      </Card>

      <Card className="p-5 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SectionTitle>Fiscal</SectionTitle>
          <Field label="CNPJ"><Input value={fiscal.cnpj || ''} onChange={(e) => set({ fiscal_data: { ...fiscal, cnpj: e.target.value } })} /></Field>
          <Field label="Company Name"><Input value={fiscal.company_name || ''} onChange={(e) => set({ fiscal_data: { ...fiscal, company_name: e.target.value } })} /></Field>
          <Field label="Trade Name"><Input value={fiscal.trade_name || ''} onChange={(e) => set({ fiscal_data: { ...fiscal, trade_name: e.target.value } })} /></Field>
        </div>
      </Card>

      <Card className="p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <SectionTitle>CPC Tiers Configuration</SectionTitle>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => set({ cpc_tiers: DEFAULT_TIERS.map((t) => ({ ...t })) })}>⤓ Load Default Tiers</Button>
            <Button variant="outline" onClick={() => set({ cpc_tiers: [...(c.cpc_tiers || []), { min: '', max: '', cpc: '' }] })}>+ Add Tier</Button>
          </div>
        </div>
        <div className="rounded-lg bg-sky-50 border border-sky-200 text-sky-800 text-xs px-4 py-3 mb-3 space-y-1">
          <p className="font-semibold">Configure CPC by click ranges:</p>
          <p>Each tier defines a CPC for a click range. The system calculates the cost incrementally based on accumulated clicks.</p>
          <p><strong>Example:</strong> 0–3,000 clicks = $0.40 CPC, 3,001–15,000 = $0.36 CPC, etc.</p>
          <p><strong>Note:</strong> leave "Max" empty on the last tier (unlimited).</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
          <Field label="Default CPC ($)" help="used when there are no tiers"><Input type="number" step={0.0001} value={c.cpc ?? ''} onChange={(e) => set({ cpc: e.target.value })} /></Field>
        </div>
        <TiersEditor tiers={c.cpc_tiers || []} onChange={(t) => set({ cpc_tiers: t })} />
      </Card>

      <Card className="p-5 mb-5">
        <SectionTitle>Branding</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
          <Field label="Branding enabled"><div className="pt-1"><Switch checked={!!c.branding_enabled} onChange={(v) => set({ branding_enabled: v })} labelOn="Enabled" labelOff="Disabled" /></div></Field>
          <Field label="Branding CPC ($)"><Input type="number" step={0.0001} value={c.branding_cpc ?? ''} onChange={(e) => set({ branding_cpc: e.target.value })} /></Field>
        </div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold text-slate-500">Branding CPC Tiers</div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => set({ branding_cpc_tiers: DEFAULT_TIERS.map((t) => ({ ...t })) })}>⤓ Load Default</Button>
            <Button variant="outline" onClick={() => set({ branding_cpc_tiers: [...(c.branding_cpc_tiers || []), { min: '', max: '', cpc: '' }] })}>+ Add Tier</Button>
          </div>
        </div>
        <TiersEditor tiers={c.branding_cpc_tiers || []} onChange={(t) => set({ branding_cpc_tiers: t })} />
      </Card>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <SectionTitle>Users</SectionTitle>
          <Button variant="outline" onClick={() => setNewUsers([...newUsers, { email: '', password: '', status: 'active', role: 'client' }])}>+ Add User</Button>
        </div>

        {isEdit && (
          (c.users || []).length > 0 ? (
            <table className="w-full text-sm mb-4">
              <thead><tr className="text-left text-xs uppercase text-slate-500 bg-surface-muted"><th className="px-3 py-2 font-medium">Email</th><th className="px-3 py-2 font-medium">Status</th><th className="px-3 py-2 font-medium">Cognito ID</th><th className="px-3 py-2 font-medium">Created</th><th className="px-3 py-2 font-medium text-right">Actions</th></tr></thead>
              <tbody className="divide-y divide-border">
                {(c.users || []).map((u, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2">{u.email}</td>
                    <td className="px-3 py-2"><Badge tone={u.status === 'active' ? 'green' : 'neutral'}>{u.status || 'active'}</Badge></td>
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-400">{(u.cognito_user_id || '').slice(0, 18) || '—'}</td>
                    <td className="px-3 py-2 text-slate-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <Button variant="outline" onClick={() => loginAs(u)} disabled={impersonating !== null}
                        title="Troca a sua sessão pela deste usuário por 1h (Log Out volta ao seu login)">
                        {impersonating === (u.email || u.cognito_user_id) ? '…' : '→ Login as user'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="text-sm text-slate-400 mb-4">No users registered.</p>
        )}

        {newUsers.length > 0 && (
          <div className="space-y-3">
            <div className="text-xs font-semibold text-slate-500">New users (will be created in Cognito on save)</div>
            {newUsers.map((u, i) => (
              // items-start: com items-end o help do password empurrava o input
              // pra cima e desalinhava Email/Password. Labels no topo alinham tudo.
              <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_150px_130px_40px] gap-2 items-start">
                <Field label="Email"><Input type="email" value={u.email} onChange={(e) => { const nx = [...newUsers]; nx[i].email = e.target.value; setNewUsers(nx); }} placeholder="user@company.com" /></Field>
                <Field label="Password" help="minimum 8 characters"><Input type="password" value={u.password} onChange={(e) => { const nx = [...newUsers]; nx[i].password = e.target.value; setNewUsers(nx); }} placeholder="••••••••" /></Field>
                <Field label="Permission" help={u.role === 'admin' ? 'full backoffice access' : 'campaigns, destinations, billing'}>
                  <Select value={u.role} onChange={(e) => { const nx = [...newUsers]; nx[i].role = e.target.value; setNewUsers(nx); }}>
                    <option value="client">client (limited)</option><option value="admin">admin (full)</option>
                  </Select>
                </Field>
                <Field label="Status">
                  <Select value={u.status} onChange={(e) => { const nx = [...newUsers]; nx[i].status = e.target.value; setNewUsers(nx); }}>
                    <option value="active">active</option><option value="inactive">inactive</option>
                  </Select>
                </Field>
                <button type="button" onClick={() => setNewUsers(newUsers.filter((_, j) => j !== i))} className="mt-6 h-9 text-rose-500 hover:text-rose-700 text-lg">×</button>
              </div>
            ))}
            {/* Mesmo save() do Update do topo — o botão aqui só deixa óbvio onde criar. */}
            <div className="pt-1">
              <Button onClick={save} disabled={saving || !newUsers.some((u) => u.email.trim() && u.password.length >= 8)}>
                {saving ? 'Saving…' : `✓ Save & create ${newUsers.filter((u) => u.email.trim() && u.password.length >= 8).length || ''} user(s)`}
              </Button>
              <p className="text-[11px] text-slate-400 mt-1.5">Creates the users above in Cognito (also saves the client, same as Update).</p>
            </div>
          </div>
        )}
        <p className="text-[11px] text-slate-400 mt-3">
          The account is created in Cognito with the password provided (permanent, no welcome email). The backend only <strong>creates</strong> users —
          editing or removing an existing user is not supported by the current API.
        </p>
      </Card>
    </Layout>
  );
}
